import { useEffect, useRef, useState } from "react";
import { multiplierAt, timeToReach } from "@karrtopelka/game-api";
import { haptic } from "@karrtopelka/game-kit";
import { colors } from "@karrtopelka/ui/theme";

/**
 * The climb, casino edition: a live line chart rockets up behind the multiplier —
 * the classic crash-game graph. The camera zooms out as the number grows (both axes
 * rescale), the line glows money-green, goes gold when you lock a cashout, and slams
 * red at the crash.
 *
 * The path is sampled analytically from the shared curve every frame (fixed cost, no
 * point accumulation). Animation timing is anchored at mount via performance.now();
 * the server still bounds the award at cashout, so it's fair.
 *
 * Desktop notes: the board goes widescreen (16:10) at lg — the SVG stays in 0-100
 * percent space with preserveAspectRatio="none", so every stroke uses
 * vector-effect="non-scaling-stroke" (px widths) and the tip glow is an HTML dot
 * positioned by percentage; nothing distorts at any aspect ratio. Spacebar/enter
 * cash out on keyboards.
 */

const SAMPLES = 140;
/** Y-axis ticks worth labelling, chosen to whatever the camera currently shows. */
const TICKS = [1.5, 2, 3, 5, 7, 10, 15, 20, 30, 50, 75, 100, 150, 200, 300, 500, 1000];

export function Climb({
  crashPoint,
  onCashout,
  onCrashReached,
}: {
  crashPoint: number;
  /** Fires once, when the player taps cash out, with the displayed multiplier. */
  onCashout: (multiplier: number) => void;
  /** Fires once, when the climb reaches the crash point and stops. */
  onCrashReached: () => void;
}) {
  const [frame, setFrame] = useState({ mult: 1, elapsed: 0 });
  const [cashedAt, setCashedAt] = useState<number | null>(null);
  const [crashed, setCrashed] = useState(false);
  const startPerf = useRef(performance.now());
  const raf = useRef<number | null>(null);
  const cashed = useRef(false);

  useEffect(() => {
    const loop = () => {
      const elapsed = (performance.now() - startPerf.current) / 1000;
      const m = multiplierAt(elapsed);
      if (m >= crashPoint) {
        setFrame({ mult: crashPoint, elapsed: timeToReach(crashPoint) });
        setCrashed(true);
        haptic.tooSoon();
        onCrashReached();
        return; // freeze the chart at the crash point
      }
      setFrame({ mult: m, elapsed });
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crashPoint]);

  const cashOut = () => {
    if (cashed.current || crashed) return;
    cashed.current = true;
    const at = frame.mult;
    setCashedAt(at);
    haptic.newBest();
    onCashout(at); // lock the win server-side; the climb keeps running
  };

  // Desktop: spacebar/enter cash out. The ref keeps the latest closure (frame.mult
  // changes every rAF tick) without re-binding the listener; cashOut self-guards, so
  // key repeat and a focused-button double-fire are harmless.
  const cashOutRef = useRef(cashOut);
  cashOutRef.current = cashOut;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.code !== "Enter") return;
      e.preventDefault(); // no page scroll, no focused-button re-activation
      cashOutRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const { mult, elapsed } = frame;

  // --- camera ---
  const tMax = Math.max(elapsed * 1.12, 5);
  const yMax = Math.max(mult * 1.3, 2);
  const X = (t: number) => (t / tMax) * 100;
  const Y = (m: number) => 96 - ((m - 1) / (yMax - 1)) * 90;

  // --- the curve, sampled fresh each frame ---
  let line = `M 0 ${Y(1).toFixed(2)}`;
  for (let i = 1; i <= SAMPLES; i++) {
    const t = (elapsed * i) / SAMPLES;
    line += ` L ${X(t).toFixed(2)} ${Y(multiplierAt(t)).toFixed(2)}`;
  }
  const tipX = X(elapsed);
  const tipY = Y(mult);
  const area = `${line} L ${tipX.toFixed(2)} 100 L 0 100 Z`;

  const ticks = TICKS.filter((v) => v < yMax * 0.97).slice(-4);
  const lineColor = crashed ? colors.danger : cashedAt !== null ? colors.gold : colors.win;

  // The number heats up with the multiplier: white → money green → gold → brand pink.
  const numClass = crashed
    ? "text-danger"
    : cashedAt !== null
      ? "text-win"
      : mult >= 15
        ? "text-accent"
        : mult >= 5
          ? "text-gold"
          : mult >= 2
            ? "text-win"
            : "text-text";

  return (
    <div className="flex w-full max-w-md animate-fade-up flex-col items-center gap-6 lg:max-w-2xl">
      <div
        className={`relative aspect-square w-full overflow-hidden rounded-lg border bg-surface lg:aspect-[16/10] ${
          crashed
            ? "animate-flash-bust border-danger/50"
            : cashedAt !== null
              ? "border-gold/40"
              : "border-border"
        }`}
      >
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="climb-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={lineColor} stopOpacity="0.28" />
              <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* y-grid at the multiplier ticks the camera currently shows */}
          {ticks.map((v) => (
            <line
              key={v}
              x1="0"
              x2="100"
              y1={Y(v)}
              y2={Y(v)}
              stroke={colors.border}
              strokeWidth="1"
              strokeDasharray="4 7"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* the player's locked level rides the chart in gold */}
          {cashedAt !== null && !crashed && (
            <line
              x1="0"
              x2="100"
              y1={Y(cashedAt)}
              y2={Y(cashedAt)}
              stroke={colors.gold}
              strokeWidth="1.5"
              strokeDasharray="6 6"
              vectorEffect="non-scaling-stroke"
            />
          )}

          <path d={area} fill="url(#climb-area)" />
          <path
            d={line}
            fill="none"
            stroke={lineColor}
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* tip glow lives in HTML space so it stays round at any aspect ratio;
            at the crash it pops via animate-burst — a little ember */}
        <div
          className={`pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${
            crashed ? "animate-burst" : ""
          }`}
          style={{
            left: `${tipX}%`,
            top: `${tipY}%`,
            backgroundColor: lineColor,
            boxShadow: `0 0 12px ${lineColor}, 0 0 28px ${lineColor}66`,
          }}
          aria-hidden
        />

        {/* hairline scanlines — lit-cabinet texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03] [background:repeating-linear-gradient(0deg,#ffffff_0_1px,transparent_1px_3px)]"
          aria-hidden
        />

        {/* tick labels live OUTSIDE the squished svg coordinate space */}
        <div className="pointer-events-none absolute inset-0">
          {ticks.map((v) => (
            <span
              key={v}
              className="absolute right-1.5 -translate-y-1/2 font-mono text-[10px] text-text-faint lg:right-2 lg:text-xs"
              style={{ top: `${Y(v)}%` }}
            >
              {v}x
            </span>
          ))}
        </div>

        <div className="pointer-events-none relative flex h-full flex-col items-center justify-center">
          <span
            className={`font-display text-7xl font-extrabold tabular-nums lg:text-8xl ${
              crashed ? "animate-shake" : ""
            } ${numClass}`}
          >
            {mult.toFixed(2)}x
          </span>
          {crashed ? (
            <span className="mt-2 text-sm font-bold uppercase tracking-wide text-danger">
              💥 crashed
            </span>
          ) : cashedAt !== null ? (
            <span className="mt-2 text-sm text-text-muted">
              locked{" "}
              <span className="font-mono font-bold text-gold">
                {cashedAt.toFixed(2)}x
              </span>{" "}
              · watching the wreck…
            </span>
          ) : (
            <span className="mt-2 animate-breathe text-sm text-text-faint">
              <span className="lg:hidden">tap to cash out</span>
              <span className="hidden lg:inline">hit space to cash out</span>
            </span>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={cashOut}
        disabled={cashed.current || crashed}
        className={`w-full rounded-md px-4 py-4 text-lg font-bold transition-all duration-200 active:scale-[0.98] enabled:hover:-translate-y-0.5 enabled:hover:brightness-110 ${
          crashed
            ? "border border-border text-text-faint"
            : cashedAt !== null
              ? "bg-gold text-bg"
              : "bg-win text-bg shadow-glow-win"
        }`}
      >
        {crashed
          ? "gone"
          : cashedAt !== null
            ? `locked ${cashedAt.toFixed(2)}x ✓`
            : `CASH OUT · ${mult.toFixed(2)}x`}
      </button>
    </div>
  );
}
